/**
 * Wire types for `/api/reports/*`.
 *
 * GENERATED-DERIVED. Every shape below is an alias of — or is projected
 * from — `components['schemas'][...]` in `src/api/generated/api.ts`, which
 * `pnpm openapi:codegen --from=staging` refreshes from the deployed BE
 * contract. Nothing here restates a BE Zod schema by hand, so a field
 * rename, a nullability change or an added enum member becomes a compile
 * error in this file (or at the first call site that reads the changed
 * field) rather than a silent disagreement that surfaces as a blank
 * widget. The NAMES are the FE-facing ones the feature already uses; the
 * generated names they map to are noted wherever the two differ.
 *
 * Two shapes deserve a note before you read them:
 *
 * - **Series and per-project rows are SPARSE TRIPLES**, `[index, points,
 *   items]`, not dense arrays. A 365-bucket window over 40 contributors
 *   is mostly zeros, and the dense encoding is what pushes the payload
 *   past a megabyte. `deriveReport` densifies once on arrival so no
 *   widget ever indexes a sparse array by bucket.
 * - **Bucket keys are STRINGS in the viewer's timezone** (`2026-09-12`,
 *   `2026-W37`, `2026-09`) and the `start` instant beside each one is
 *   only there for tooltips that want an exact moment. Label from the
 *   KEY. Labelling from `start` re-derives the calendar day in the
 *   BROWSER's zone, which is the same zone only by coincidence.
 *
 * The generated types are DEEPLY READONLY (openapi-typescript emits
 * `readonly` on every property and array). Read paths take that as-is —
 * a report payload is never mutated. The two places the FE genuinely
 * writes are the layout draft's widget list and a widget's config, and
 * those are the only types here wrapped in `Mutable`.
 */

import type { components } from '@/api/generated/api'

type Schemas = components['schemas']

/** Strip the generated `readonly` modifiers, preserving optionality. */
type Mutable<T> = { -readonly [K in keyof T]: T[K] }

/**
 * `ReportWidget` on the wire is a DISCRIMINATED UNION — one member per
 * widget type, each with its own `.strict()` config. These helpers reach
 * into a named member so the FE's flatter draft shapes stay pinned to it.
 */
type WireWidget = Schemas['ReportWidget']
type WireWidgetOf<T extends WireWidget['type']> = Extract<WireWidget, { type: T }>
type WireConfigOf<T extends WireWidget['type']> = NonNullable<WireWidgetOf<T>['config']>

/* -------------------------------------------------------------------------- */
/*                           Performance report                               */
/* -------------------------------------------------------------------------- */

/** One time bucket. `key` is viewer-local; `start` is the instant it opens. */
export type ReportBucket = Schemas['ReportBucket']

export type ReportWindow = Schemas['ReportWindow']

/** Bucket granularity. Chosen by the FE from the window length. */
export type ReportGranularity = ReportWindow['granularity']

/**
 * `[index, points, items]`. The index is a position in
 * `window.buckets` (series) or in `meta.projects` (byProject).
 */
export type ReportTriple = Schemas['ReportTriple']

export type ReportTotals = Schemas['ReportTotals']

export type ReportUser = Schemas['ReportUser']

/** A user row's human/agent discriminator (`users.kind`). */
export type ReportUserKind = ReportUser['kind']

/** Generated name: `ReportProjectMeta`. `code` is nullable on the wire. */
export type ReportProjectMeta = Schemas['ReportProjectMeta']

export type PerformanceReport = Schemas['PerformanceReport']

/** `GET /api/reports/performance` — the `{ data: ... }` envelope. */
export type PerformanceReportResponse = Schemas['PerformanceReportResponse']

/**
 * What a widget counts. Same enum on every widget config, so it is read
 * off one of them rather than restated.
 */
export type ReportMetric = NonNullable<WireConfigOf<'leaderboard'>['metric']>

/**
 * People filter. Applied CLIENT-side; never sent on the wire in v1 —
 * but the BE accepts it on a saved config, which is where the enum
 * lives.
 */
export type ReportKindFilter = NonNullable<WireConfigOf<'leaderboard'>['kind']>

/* -------------------------------------------------------------------------- */
/*                           Layout persistence                               */
/* -------------------------------------------------------------------------- */

/**
 * By model reads `GET /api/reports/models` rather than the performance
 * report. It is named here until the generated wire union carries it; once
 * it does the two halves of the union overlap and this stays correct
 * without an edit.
 */
export type AiReportWidgetType = 'byModel'

/**
 * Widget types an earlier release shipped and later retired. A saved
 * layout or a cached draft can still hold one; reading drops it silently
 * instead of keeping it as an "Unavailable widget" the user never added.
 */
export const RETIRED_WIDGET_TYPES: readonly string[] = ['peopleVsAi', 'aiEfficiency']

export type ReportWidgetType = WireWidget['type'] | AiReportWidgetType

/**
 * The runtime list behind `isKnownType`. `satisfies` pins every entry to
 * the wire union, so a renamed or retired BE widget type fails to
 * compile here; an ADDED one fails in `WIDGET_REGISTRY`, which is keyed
 * `Record<ReportWidgetType, …>`.
 */
export const REPORT_WIDGET_TYPES = [
  'leaderboard',
  'rankedBar',
  'trendLine',
  'shareDonut',
  'throughputStacked',
  'pointsByProject',
  'kpiTile',
  'myPerformance',
  'byModel',
  // RESERVED, not on the wire in v1: `score` (the composite "3mote
  // Score"). It needs its own ADR and a formula that reads the
  // transitions ledger; the name is held here so a future addition
  // doesn't collide with a widget someone invents in the meantime.
] as const satisfies readonly ReportWidgetType[]

export type ReportKpiVariant = NonNullable<WireConfigOf<'kpiTile'>['kpiVariant']>

export const REPORT_KPI_VARIANTS = [
  'totalPoints',
  'totalItems',
  'contributors',
  'unassigned',
] as const satisfies readonly ReportKpiVariant[]

/**
 * A widget's own period. `null` (and an absent field) both mean "follow
 * the dashboard" — the two are equivalent on read; `sanitizeForSave`
 * drops the key rather than writing an explicit null. `undefined` is
 * excluded here because the absent case is expressed by the optional
 * `config.periodOverride` key itself.
 */
export type ReportPeriodOverride = Exclude<WireConfigOf<'leaderboard'>['periodOverride'], undefined>

export type ReportPeriodPreset = Extract<
  NonNullable<ReportPeriodOverride>,
  { preset: string }
>['preset']

export const REPORT_PERIOD_PRESETS = [
  'today',
  'this-week',
  'last-week',
  'this-month',
  'last-month',
  'this-quarter',
  'ytd',
  'last-12-months',
] as const satisfies readonly ReportPeriodPreset[]

/**
 * The union every widget body is typed against: the widest config any
 * wire member carries, flattened and made writable.
 *
 * Deliberately a plain merge of optional fields rather than the wire's
 * discriminated union: the registry narrows per type, and a
 * discriminated union here would force every generic helper
 * (`sanitizeForSave`, `layoutsEqual`, the config popover's `patch`) to
 * branch. `leaderboard` contributes the ranked fields (`topN`, `sort`)
 * and `kpiTile` contributes `kpiVariant`; between them they cover every
 * key the BE names. Which keys a given type may actually carry is
 * enforced on the way out, in `sanitizeConfig`.
 */
export type ReportWidgetConfig = Mutable<WireConfigOf<'leaderboard'> & WireConfigOf<'kpiTile'>>

/** Grid placement, in 12-column units, taken from the wire member. */
export type ReportWidgetGrid = Mutable<Pick<WireWidgetOf<'leaderboard'>, 'x' | 'y' | 'w' | 'h'>>

/**
 * A widget as the DRAFT holds it.
 *
 * Structurally the wire member with the per-type discrimination relaxed,
 * and that relaxation is load-bearing in both directions: `parseSavedLayout`
 * must be able to hold a type this build doesn't know (it renders as a
 * removable "Unavailable widget" frame instead of vanishing), and every
 * generic helper reads `config` without knowing the type. Geometry, the
 * type union and the config keys are all projected from the wire union
 * above, so the only thing that can drift is the discrimination itself.
 */
export interface ReportWidget extends ReportWidgetGrid {
  /** Stable id. Deterministic for the default layout, a UUID for adds. */
  id: WireWidgetOf<'leaderboard'>['id']
  type: ReportWidgetType
  config: ReportWidgetConfig
}

export type ReportLayout = NonNullable<Schemas['ReportLayout']>

export const REPORT_LAYOUT_SCHEMA_VERSION: ReportLayout['schemaVersion'] = 1
export const REPORT_LAYOUT_MAX_WIDGETS = 30
/** The grid is a 12-column contract shared with the BE's bounds check. */
export const REPORT_GRID_COLUMNS = 12

/** `GET /api/reports/layout` — `layout: null` when the user has never saved. */
export type ReportLayoutResponse = Schemas['ReportLayoutResponse']['data']

/**
 * A stored layout as the READ guard sees it, with `schemaVersion` widened
 * back to `number`.
 *
 * The wire type pins it to the literal `1`, which is right for the
 * CURRENT contract and wrong for the row `parseSavedLayout` is written to
 * survive: a layout saved by a future release, read by this bundle. That
 * version check is the one case that falls back to the default wholesale,
 * so the value has to be representable for the guard to mean anything.
 */
export type StoredReportLayout = Omit<ReportLayout, 'schemaVersion' | 'widgets'> & {
  schemaVersion: number
  /**
   * Widened to the draft row for the same reason `schemaVersion` is: a
   * stored layout can name a type the generated union hasn't caught up
   * with (By model) or one this build has never heard of.
   */
  widgets: readonly (Omit<ReportWidget, 'config'> & { config?: ReportWidgetConfig })[]
}

/**
 * `PUT /api/reports/layout` body.
 *
 * `widgets` is widened from the wire's discriminated union to the draft's
 * `ReportWidget` for the same reason that type exists: the FE builds the
 * body from draft rows. `sanitizeForSave` is what guarantees each row is
 * a valid member of the union by the time it ships, and the BE
 * re-validates per type regardless.
 */
export type ReportLayoutInput = Omit<Schemas['ReportLayoutInput'], 'widgets'> & {
  widgets: readonly ReportWidget[]
}
